import { useMemo } from 'react';
import { collection, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useFirestoreQuery } from './useFirestoreQuery';

/**
 * Live roster of AI agents for a company (`ai_agents` collection).
 * Sorted client-side so agents without `createdAt` are still included
 * (Firestore `orderBy` would silently drop those docs).
 * @param {string | null | undefined} companyId
 */
export function useCompanyAiAgents(companyId) {
  const agentsQuery = useMemo(() => {
    if (!companyId) return null;
    return query(
      collection(db, 'ai_agents'),
      where('companyId', '==', companyId),
    );
  }, [companyId]);

  const { data, loading, error } = useFirestoreQuery(agentsQuery, {
    enabled: Boolean(companyId),
  });

  const agents = useMemo(() => {
    const rows = Array.isArray(data) ? [...data] : [];
    rows.sort((a, b) => {
      const aMs = a?.createdAt?.toMillis?.() ??
        (a?.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0);
      const bMs = b?.createdAt?.toMillis?.() ??
        (b?.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0);
      return bMs - aMs;
    });
    return rows;
  }, [data]);

  return {
    agents,
    loading,
    error,
  };
}
